import { misc, neutral, primary } from "app/themes/colors";

/**
 * Backdrops offered behind a captured page. Four kinds, because they are drawn
 * by four different bits of canvas code — see `compose-screenshot.util.ts`.
 *
 * `colors` is read only by `gradient` (two stops, top-left → bottom-right) and
 * `solid` (one fill). `blur` derives its backdrop from the capture itself and
 * `none` leaves the canvas transparent, so both ignore it.
 */
export type ScreenshotBackgroundKind = "blur" | "gradient" | "solid" | "none";

export type ScreenshotBackgroundId =
  | "blur"
  | "aurora"
  | "dusk"
  | "mint"
  | "deep"
  | "paper"
  | "none";

export interface ScreenshotBackground {
  id: ScreenshotBackgroundId;
  /** Shown under the swatch row and used as the swatch's accessible name. */
  label: string;
  kind: ScreenshotBackgroundKind;
  colors: string[];
  /**
   * Whether the backdrop is dark enough that the white wordmark and the light
   * hairline around the frame are the legible choice. Declared rather than
   * measured: these are seven fixed presets, and a luminance calculation would
   * be a second source of truth for something we already know.
   */
  isDark: boolean;
}

/**
 * Palette values only — `app/themes/colors`, never a hex literal, so the frame
 * follows the brand if the brand moves.
 */
export const SCREENSHOT_BACKGROUNDS: ScreenshotBackground[] = [
  {
    id: "aurora",
    label: "Aurora",
    kind: "gradient",
    colors: [primary[200], primary[400]],
    isDark: true
  },
  {
    id: "dusk",
    label: "Dusk",
    kind: "gradient",
    colors: [misc.suno, primary[600]],
    isDark: true
  },
  {
    id: "blur",
    label: "Blurred page",
    kind: "blur",
    colors: [],
    // The app is light-themed, so the blurred echo of a page is light too, and
    // the compositor lays a pale scrim over it besides.
    isDark: false
  },
  {
    id: "mint",
    label: "Mint",
    kind: "solid",
    colors: [primary[50]],
    isDark: false
  },
  {
    id: "deep",
    label: "Deep teal",
    kind: "solid",
    colors: [primary[500]],
    isDark: true
  },
  {
    id: "paper",
    label: "Paper",
    kind: "solid",
    colors: [neutral[100]],
    isDark: false
  },
  {
    id: "none",
    label: "Transparent",
    kind: "none",
    colors: [],
    // A transparent PNG is usually dropped onto a light slide or doc.
    isDark: false
  }
];

export const DEFAULT_SCREENSHOT_BACKGROUND_ID: ScreenshotBackgroundId =
  "aurora";

export type ScreenshotPaddingId = "snug" | "comfortable" | "roomy";

export interface ScreenshotPadding {
  id: ScreenshotPaddingId;
  label: string;
  /** Margin around the frame as a fraction of the capture's width. */
  ratio: number;
}

export const SCREENSHOT_PADDINGS: ScreenshotPadding[] = [
  { id: "snug", label: "Snug", ratio: 0.025 },
  { id: "comfortable", label: "Comfortable", ratio: 0.055 },
  { id: "roomy", label: "Roomy", ratio: 0.09 }
];

export const DEFAULT_SCREENSHOT_PADDING_ID: ScreenshotPaddingId = "comfortable";

export interface ScreenshotStyle {
  backgroundId: ScreenshotBackgroundId;
  paddingId: ScreenshotPaddingId;
}

export const DEFAULT_SCREENSHOT_STYLE: ScreenshotStyle = {
  backgroundId: DEFAULT_SCREENSHOT_BACKGROUND_ID,
  paddingId: DEFAULT_SCREENSHOT_PADDING_ID
};

export const findBackground = (
  id: ScreenshotBackgroundId
): ScreenshotBackground =>
  SCREENSHOT_BACKGROUNDS.find((background) => background.id === id) ??
  SCREENSHOT_BACKGROUNDS[0];

export const findPadding = (id: ScreenshotPaddingId): ScreenshotPadding =>
  SCREENSHOT_PADDINGS.find((padding) => padding.id === id) ??
  SCREENSHOT_PADDINGS[1];

/**
 * Narrows whatever came back from localStorage. A stored style is a per-browser
 * convenience, so anything unrecognised — a value from an older release, a
 * hand-edited entry — falls back to the default rather than throwing.
 */
export const parseScreenshotStyle = (value: unknown): ScreenshotStyle => {
  if (typeof value !== "object" || value === null) {
    return DEFAULT_SCREENSHOT_STYLE;
  }
  const candidate = value as Partial<ScreenshotStyle>;
  const background = SCREENSHOT_BACKGROUNDS.some(
    (item) => item.id === candidate.backgroundId
  );
  const padding = SCREENSHOT_PADDINGS.some(
    (item) => item.id === candidate.paddingId
  );
  return {
    backgroundId: background
      ? (candidate.backgroundId as ScreenshotBackgroundId)
      : DEFAULT_SCREENSHOT_BACKGROUND_ID,
    paddingId: padding
      ? (candidate.paddingId as ScreenshotPaddingId)
      : DEFAULT_SCREENSHOT_PADDING_ID
  };
};
